import { Component, computed, effect, inject, input, NgZone, signal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { UserWidgetSetting } from '@libs/types/src/services/types/service-settings-user-widgets.type';
import {
    DashboardFinancialBalanceObject,
} from '@libs/types/src/services/object-type/dashboard/dashboard-financial-balance.object';
import { UIChart } from '@openng/optimus-ui/chart';
import { Chart, ChartData, ChartOptions, TooltipModel } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { TimePeriodEnum } from '@libs/types/src/common/enum/timePeriod.enum';
import { DashboardChartWidgetStore } from '../dashboard-chart-widget.store';
import { dashboardChartTooltipPosition, dashboardServiceIds } from '../dashboard-chart-widget.utils';
import { dashboardFinancialBalanceFragment } from '../dashboard-chart-widget.query';
import { LoadingComponent } from '../../../../../../components/kit/loading/loading.component';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';
import { DashboardChartTooltipComponent } from '../dashboard-chart-tooltip/dashboard-chart-tooltip.component';
import { DashboardChartTooltipData } from '../dashboard-chart-tooltip/dashboard-chart-tooltip.types';
import {
    DateScrollerInputComponent,
} from '../../../../../../components/kit/input/date-scroller-input-v2/date-scroller-input.component';
import {
    DateScrollerRange,
} from '../../../../../../components/kit/input/date-scroller-input-v2/date-scroller-input.types';

@Component({
    selector: 'app-financial-balance-widget',
    templateUrl: './financial-balance-widget.component.html',
    styleUrl: './financial-balance-widget.component.scss',
    providers: [DashboardChartWidgetStore],
    imports: [
        FormField,
        DateScrollerInputComponent,
        UIChart,
        LoadingComponent,
        EmptyStateComponent,
        DashboardChartTooltipComponent,
    ],
})
export class FinancialBalanceWidgetComponent {
    protected readonly store = inject(DashboardChartWidgetStore);
    private readonly translate = inject(TranslateService);
    private readonly zone = inject(NgZone);
    private readonly colors = {
        previousPaid: '#5b8def',
        previousUnpaid: '#f39c4a',
        currentPaid: '#8a8f98',
        currentUnpaid: '#f2c500',
    } as const;

    public readonly items = input<{ serviceId: string; widget: UserWidgetSetting }[]>([]);

    protected readonly dateFormData = signal({ date: new Date() as Date | null });
    protected readonly dateForm = form(this.dateFormData);
    protected readonly period = signal<DateScrollerRange>(this.currentMonth());
    protected readonly loading = this.store.loadingFor('getDashboardFinancialBalance');
    protected readonly rows = computed<DashboardFinancialBalanceObject[]>(
        () => this.store.data.getDashboardFinancialBalance?.() ?? [],
    );
    protected readonly chartRows = computed<DashboardFinancialBalanceObject[]>(() => {
        const rows = this.rows();
        const totals = rows.reduce((result, row) => ({
            previousPaid: result.previousPaid + row.previousPaid,
            previousUnpaid: result.previousUnpaid + row.previousUnpaid,
            currentPaid: result.currentPaid + row.currentPaid,
            currentUnpaid: result.currentUnpaid + row.currentUnpaid,
        }), { previousPaid: 0, previousUnpaid: 0, currentPaid: 0, currentUnpaid: 0 });
        return [...rows, {
            folderId: 0,
            personId: 0,
            client: this.translate.instant('dashboard.financialBalance.total'),
            ...totals,
        }];
    });
    protected readonly chartData = computed<ChartData<'bar'>>(() => {
        const values = this.chartRows();
        return {
            labels: values.map(row => row.client),
            datasets: [
                this.barDataset(this.balanceLabel(-1, 'paid'), this.colors.previousPaid, values.map(row => row.previousPaid)),
                this.barDataset(this.balanceLabel(-1, 'unpaid'), this.colors.previousUnpaid, values.map(row => row.previousUnpaid)),
                this.barDataset(this.balanceLabel(0, 'paid'), this.colors.currentPaid, values.map(row => row.currentPaid)),
                this.barDataset(this.balanceLabel(0, 'unpaid'), this.colors.currentUnpaid, values.map(row => row.currentUnpaid)),
            ],
        };
    });
    protected readonly chartTooltip = signal<DashboardChartTooltipData | null>(null);
    protected readonly chartOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: true, axis: 'x' },
        plugins: {
            legend: { position: 'bottom', reverse: true },
            tooltip: {
                enabled: false,
                external: ({ chart, tooltip }) => this.updateChartTooltip(chart, tooltip),
            },
        },
        scales: {
            x: { stacked: true },
            y: { stacked: true, beginAtZero: true },
        },
    };

    constructor() {
        effect(() => {
            const serviceIds = dashboardServiceIds(this.items());
            const period = this.period();
            if (!serviceIds.length) return;

            this.store.api.getDashboardFinancialBalance({
                serviceIds,
                year: period.dateFrom.getFullYear(),
                month: period.dateFrom.getMonth() + 1,
            }, dashboardFinancialBalanceFragment).then();
        });
    }

    private updateChartTooltip(chart: Chart, tooltip: TooltipModel<'bar'>): void {
        if (!tooltip.opacity) {
            this.hideChartTooltip();
            return;
        }

        const dataIndex = tooltip.dataPoints[0]?.dataIndex;
        const row = dataIndex === undefined ? undefined : this.chartRows()[dataIndex];
        if (!row || ![row.currentUnpaid, row.currentPaid, row.previousUnpaid, row.previousPaid].some(Boolean)) {
            this.hideChartTooltip();
            return;
        }

        this.zone.run(() => this.chartTooltip.set({
            ...dashboardChartTooltipPosition(chart.width, chart.height, tooltip.caretX, tooltip.caretY),
            title: row.client,
            summary: [
                {
                    label: this.balanceLabel(0, 'unpaid'),
                    value: this.currency(row.currentUnpaid),
                    color: this.colors.currentUnpaid,
                },
                {
                    label: this.balanceLabel(0, 'paid'),
                    value: this.currency(row.currentPaid),
                    color: this.colors.currentPaid,
                },
                {
                    label: this.balanceLabel(-1, 'unpaid'),
                    value: this.currency(row.previousUnpaid),
                    color: this.colors.previousUnpaid,
                },
                {
                    label: this.balanceLabel(-1, 'paid'),
                    value: this.currency(row.previousPaid),
                    color: this.colors.previousPaid,
                },
            ],
        }));
    }

    private hideChartTooltip(): void {
        this.zone.run(() => this.chartTooltip.set(null));
    }

    private barDataset(label: string, backgroundColor: string, data: number[]) {
        return {
            label,
            data,
            backgroundColor,
            borderWidth: 0,
        };
    }

    private balanceLabel(monthOffset: number, paymentState: 'paid' | 'unpaid'): string {
        const selectedDate = this.period().dateFrom;
        const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + monthOffset, 1);
        const locale = this.translate.currentLang === 'cz' ? 'cs-CZ' : 'en-US';
        const month = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
        const capitalizedMonth = month.charAt(0).toLocaleUpperCase(locale) + month.slice(1);

        return `${capitalizedMonth} – ${this.translate.instant(`dashboard.financialBalance.${paymentState}`)}`;
    }

    private currency(value: number): string {
        return new Intl.NumberFormat(this.translate.currentLang === 'cz' ? 'cs-CZ' : 'en-US', {
            style: 'currency',
            currency: 'CZK',
            maximumFractionDigits: 2,
        }).format(value);
    }

    private currentMonth(): DateScrollerRange {
        const dateFrom = new Date();
        dateFrom.setHours(0, 0, 0, 0);
        dateFrom.setDate(1);
        const dateTo = new Date(dateFrom.getFullYear(), dateFrom.getMonth() + 1, 0, 23, 59, 59, 999);
        return { dateFrom, dateTo, type: TimePeriodEnum.Month, length: 1 };
    }

    protected readonly TimePeriodEnum = TimePeriodEnum;
}
